import type { User, AuthResponse, AIPersona, PersonaConversation, UserProfile, MyPersona } from '../store/types'

const API_BASE = '/api'

class ApiClient {
  private token: string | null = null

  setToken(token: string | null) {
    this.token = token
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    } else {
      try {
        const storage = localStorage.getItem('glovely-storage');
        if (storage) {
          const parsed = JSON.parse(storage);
          const token = parsed.state?.token;
          if (token) {
            this.token = token;
            headers['Authorization'] = `Bearer ${token}`;
          }
        }
      } catch (e) {
        console.error('Failed to retrieve token from storage', e);
      }
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
      // Preserve all error metadata for special cases (stage_locked, message_blocked, etc.)
      const error = new Error(errorData.error || `HTTP ${response.status}`)
      // Attach all error response fields to the Error object
      Object.assign(error, errorData)
      throw error
    }

    return response.json()
  }

  // Auth
  auth = {
    googleLogin: async (accessToken: string): Promise<AuthResponse> => {
      return this.request('/auth/google', {
        method: 'POST',
        body: JSON.stringify({ access_token: accessToken }),
      })
    },
    me: async (): Promise<User> => {
      return this.request('/auth/me')
    },
  }

  // Users
  users = {
    getProfile: async (): Promise<UserProfile> => {
      return this.request('/users/profile')
    },
    getUserProfile: async (userId: number): Promise<any> => {
      return this.request(`/users/${userId}/profile`)
    },
    updateProfile: async (data: any): Promise<any> => {
      return this.request('/users/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    },
    getLikes: async (): Promise<any> => {
      return this.request('/users/likes')
    },
    like: async (userId: number): Promise<any> => {
      return this.request(`/users/${userId}/like`, {
        method: 'POST',
      })
    },
    unlike: async (userId: number): Promise<any> => {
      return this.request(`/users/${userId}/like`, {
        method: 'DELETE',
      })
    },
    // Profile Answers (질문 답변)
    getProfileAnswers: async (): Promise<{
      answers: Record<string, string>
      details: Array<{
        question_id: string
        answer: string
        stage: number
        is_public: number
        created_at: string
        updated_at: string
      }>
    }> => {
      return this.request('/users/profile-answers')
    },
    updateProfileAnswers: async (answers: Array<{
      question_id: string
      answer: string
      stage: number
    }>): Promise<{
      success: boolean
      answers: Record<string, string>
      details: Array<{
        question_id: string
        answer: string
        stage: number
      }>
    }> => {
      return this.request('/users/profile-answers', {
        method: 'PUT',
        body: JSON.stringify({ answers }),
      })
    },
  }

  // Photos
  photos = {
    upload: async (photo: File): Promise<{ success: boolean; message: string; photo: any }> => {
      const formData = new FormData()
      formData.append('photo', photo)

      // For multipart/form-data, we let the browser set the Content-Type header
      const response = await fetch(`${API_BASE}/photos`, {
        method: 'POST',
        headers: {
          ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(error.error || `HTTP ${response.status}`)
      }
      return response.json();
    },
    list: async (userId: number): Promise<any[]> => {
      return this.request<{ photos: any[] }>(`/users/${userId}/photos`).then(data => data.photos);
    },
    delete: async (photoId: number): Promise<{ success: boolean }> => {
      return this.request(`/photos/${photoId}`, { method: 'DELETE' });
    }
  }

  // Match Preferences
  matchPreferences = {
    get: async (): Promise<{ preference: unknown }> => {
      return this.request('/match-preferences')
    },
    toggle: async (isActive: number, timezone?: string): Promise<{
      success: boolean
      preference: unknown
      message: string
    }> => {
      return this.request('/match-preferences', {
        method: 'PATCH',
        body: JSON.stringify({ is_active: isActive, timezone }),
      })
    },
  }

  // Personas
  personas = {
    list: async (): Promise<AIPersona[]> => {
      return this.request('/personas')
    },
    get: async (id: number): Promise<AIPersona> => {
      return this.request(`/personas/${id}`)
    },
    create: async (persona: Omit<AIPersona, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<AIPersona> => {
      return this.request('/personas', {
        method: 'POST',
        body: JSON.stringify(persona),
      })
    },
    update: async (id: number, updates: Partial<AIPersona>): Promise<AIPersona> => {
      return this.request(`/personas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      })
    },
    delete: async (id: number): Promise<void> => {
      return this.request(`/personas/${id}`, { method: 'DELETE' })
    },
  }

  // Conversations
  conversations = {
    list: async (personaId: number): Promise<PersonaConversation[]> => {
      return this.request(`/personas/${personaId}/conversations`)
    },
    send: async (
      personaId: number,
      message: string
    ): Promise<{ userMessage: PersonaConversation; aiResponse: PersonaConversation }> => {
      return this.request(`/personas/${personaId}/chat`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      })
    },
    clear: async (personaId: number): Promise<void> => {
      return this.request(`/personas/${personaId}/conversations`, {
        method: 'DELETE',
      })
    },
  }

  // Assistant (direct AI chat)
  assistant = {
    chat: async (
      messages: Array<{ role: 'user' | 'assistant'; content: string }>,
      systemPrompt?: string
    ): Promise<{ response: string }> => {
      return this.request('/assistant/chat', {
        method: 'POST',
        body: JSON.stringify({ messages, systemPrompt }),
      })
    },
  }

  // My Persona (AI 분신)
  myPersona = {
    get: async (): Promise<MyPersona> => {
      return this.request('/my-persona')
    },
    update: async (data: Partial<MyPersona>): Promise<MyPersona> => {
      return this.request('/my-persona', {
        method: 'PUT',
        body: JSON.stringify(data),
      })
    },
    learn: async (): Promise<{ success: boolean; analysis: any }> => {
      return this.request('/my-persona/learn', { method: 'POST' })
    },
  }

  // Matches
  matches = {
    list: async (sort: 'score' | 'recent' = 'score'): Promise<{ matches: unknown[]; sort: string }> => {
      return this.request(`/matches?sort=${sort}`)
    },
    getMessages: async (matchId: number): Promise<{ match: unknown; messages: unknown[] }> => {
      return this.request(`/matches/${matchId}/messages`)
    },
    sendMessage: async (matchId: number, content: string): Promise<{ message: unknown }> => {
      return this.request(`/matches/${matchId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      })
    },
    suggest: async (matchId: number): Promise<{
      suggestions: Array<{ text: string; reason: string }> | string[]
      suggestionType: 'greeting' | 'reply'
      coachTip?: string
      remaining: number
      error?: string
    }> => {
      return this.request(`/matches/${matchId}/suggest`)
    },
    takeover: async (matchId: number): Promise<void> => {
      return this.request(`/matches/${matchId}/takeover`, { method: 'POST' })
    },
    delegate: async (matchId: number): Promise<void> => {
      return this.request(`/matches/${matchId}/delegate`, { method: 'POST' })
    },
    getSuggestion: async (): Promise<{ suggestion: unknown; message: string }> => {
      return this.request('/matches/suggestions')
    },
    respondToSuggestion: async (suggestionId: number, response: 'accept' | 'reject'): Promise<{
      success: boolean
      mutualAccept: boolean
      finalMatchId: number | null
      message: string
      nextMatchAt: string
    }> => {
      return this.request(`/matches/suggestions/${suggestionId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ response }),
      })
    },
    leave: async (matchId: number): Promise<{ success: boolean; message: string }> => {
      return this.request(`/matches/${matchId}/leave`, {
        method: 'POST',
      })
    },
    setTyping: async (matchId: number, isTyping: boolean): Promise<{ success: boolean }> => {
      return this.request(`/matches/${matchId}/typing`, {
        method: 'POST',
        body: JSON.stringify({ isTyping }),
      })
    },
    shareProfile: async (matchId: number, stage: number): Promise<{ success: boolean; stage: number; bothShared: boolean }> => {
      return this.request(`/matches/${matchId}/share-profile`, {
        method: 'POST',
        body: JSON.stringify({ stage })
      })
    },
    getProfileAnswers: async (matchId: number, stage: number): Promise<{ bothShared: boolean; myAnswers: any[]; partnerAnswers: any[] }> => {
      return this.request(`/matches/${matchId}/share-profile?stage=${stage}`)
    },
    // Question Reveals API
    getAvailableQuestions: async (matchId: number): Promise<{
      available: boolean
      questions: Array<{ question_id: string; reveal_at_message_count: number; stage: number; text: string; category: string }>
      currentMessageCount: number
    }> => {
      return this.request(`/matches/${matchId}/question-reveals`)
    },
    revealQuestion: async (matchId: number, questionId: string): Promise<{
      success: boolean
      question: { id: string; text: string; category: string }
      myAnswer: string | null
      partnerAnswer: string | null
    }> => {
      return this.request(`/matches/${matchId}/question-reveals`, {
        method: 'POST',
        body: JSON.stringify({ questionId })
      })
    },
    // Mini-game API
    startMinigame: async (matchId: number): Promise<{
      success: boolean
      session_id: number
      question: string
      choices: { a: string; b: string }
      expires_at: string
    }> => {
      return this.request(`/matches/${matchId}/minigame`, {
        method: 'POST'
      })
    },
    getMinigameSession: async (matchId: number): Promise<{
      active: boolean
      session_id?: number
      question?: string
      choices?: { a: string; b: string }
      my_answer?: string | null
      other_answered?: boolean
      revealed?: boolean
      expires_at?: string
    }> => {
      return this.request(`/matches/${matchId}/minigame`)
    },
    submitMinigameAnswer: async (matchId: number, answer: 'A' | 'B'): Promise<{
      success: boolean
      both_answered: boolean
      revealed: boolean
    }> => {
      return this.request(`/matches/${matchId}/minigame`, {
        method: 'PUT',
        body: JSON.stringify({ answer })
      })
    },
    // Stage Upgrade API
    upgradeStage: async (matchId: number): Promise<{
      success: boolean
      upgraded: boolean
      newStage?: number
      message: string
    }> => {
      return this.request(`/matches/${matchId}/stage-upgrade`, {
        method: 'POST'
      })
    },
    getStageUpgradeStatus: async (matchId: number): Promise<{
      hasPendingRequest: boolean
      isRequestedByMe: boolean
      isRequestedByPartner: boolean
      currentStage: number
    }> => {
      return this.request(`/matches/${matchId}/stage-upgrade`)
    },
  }

  // Community Feed
  community = {
    // Posts
    getPosts: async (params: {
      filter?: 'all' | 'country' | 'interest'
      filterValue?: string
      sort?: 'latest' | 'popular' | 'trending'
      limit?: number
      offset?: number
    }): Promise<any> => {
      const query = new URLSearchParams()
      if (params.filter) query.append('filter', params.filter)
      if (params.filterValue) query.append('filterValue', params.filterValue)
      if (params.sort) query.append('sort', params.sort)
      if (params.limit) query.append('limit', params.limit.toString())
      if (params.offset) query.append('offset', params.offset.toString())

      return this.request(`/community/posts?${query.toString()}`)
    },

    createPost: async (content: string, hashtags?: string[]): Promise<any> => {
      return this.request('/community/posts', {
        method: 'POST',
        body: JSON.stringify({ content, hashtags }),
      })
    },

    getPost: async (postId: number): Promise<any> => {
      return this.request(`/community/${postId}`)
    },

    deletePost: async (postId: number): Promise<any> => {
      return this.request(`/community/${postId}`, {
        method: 'DELETE',
      })
    },

    // Post Likes
    likePost: async (postId: number): Promise<any> => {
      return this.request(`/community/${postId}/like`, {
        method: 'POST',
      })
    },

    unlikePost: async (postId: number): Promise<any> => {
      return this.request(`/community/${postId}/like`, {
        method: 'DELETE',
      })
    },

    // Comments
    createComment: async (postId: number, content: string, parentCommentId?: number): Promise<any> => {
      return this.request(`/community/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content, parentCommentId }),
      })
    },

    // Comment Likes
    likeComment: async (commentId: number): Promise<any> => {
      return this.request(`/community/comments/${commentId}/like`, {
        method: 'POST',
      })
    },

    unlikeComment: async (commentId: number): Promise<any> => {
      return this.request(`/community/comments/${commentId}/like`, {
        method: 'DELETE',
      })
    },

    // Anonymous Profile
    getAnonymousProfile: async (userId: number): Promise<any> => {
      return this.request(`/community/users/${userId}/anonymous-profile`)
    },

    // Profile Likes (matching boost)
    likeProfile: async (userId: number, sourcePostId?: number): Promise<any> => {
      return this.request(`/community/profile-likes/${userId}`, {
        method: 'POST',
        body: JSON.stringify({ sourcePostId }),
      })
    },

    unlikeProfile: async (userId: number): Promise<any> => {
      return this.request(`/community/profile-likes/${userId}`, {
        method: 'DELETE',
      })
    },
  }

  // Points
  points = {
    // Get user points
    getPoints: async (): Promise<{
      success: boolean
      points: {
        general_points: number
        match_points: number
        total_earned: number
        total_spent: number
        daily_quiz_streak: number
      }
      error?: string
    }> => {
      return this.request('/points')
    },

    // Get transaction history
    getTransactions: async (params?: {
      limit?: number
      offset?: number
    }): Promise<{
      success: boolean
      transactions: Array<{
        id: number
        amount: number
        source: string
        description: string | null
        created_at: string
      }>
      total: number
      hasMore: boolean
      error?: string
    }> => {
      const query = new URLSearchParams()
      if (params?.limit) query.append('limit', params.limit.toString())
      if (params?.offset) query.append('offset', params.offset.toString())

      return this.request(`/points/transactions?${query.toString()}`)
    },

    // Spend points
    spend: async (params: {
      amount: number
      source: string
      source_id?: number
      description?: string
      metadata?: Record<string, any>
    }): Promise<{
      success: boolean
      new_balance: number
      error?: string
    }> => {
      return this.request('/points/spend', {
        method: 'POST',
        body: JSON.stringify(params),
      })
    },
  }

  // Premium features API (새 포인트 사용처)
  premium = {
    // Profile Boost (100 GP / 24시간)
    getBoostStatus: async (): Promise<{
      hasActiveBoost: boolean
      expiresAt?: string
      startedAt?: string
      boostCost?: number
      boostDurationHours?: number
    }> => {
      return this.request('/premium/boost')
    },

    activateBoost: async (): Promise<{
      success: boolean
      message: string
      expiresAt?: string
      pointsUsed?: number
      newBalance?: number
    }> => {
      return this.request('/premium/boost', { method: 'POST' })
    },

    // Super Like (30 GP)
    sendSuperLike: async (receiverId: number): Promise<{
      success: boolean
      message: string
      pointsUsed?: number
      newBalance?: number
    }> => {
      return this.request('/premium/superlike', {
        method: 'POST',
        body: JSON.stringify({ receiverId }),
      })
    },

    getSuperLikesReceived: async (): Promise<{
      superLikes: Array<{
        id: number
        sender_id: number
        created_at: string
        username: string
        picture: string | null
      }>
      cost: number
    }> => {
      return this.request('/premium/superlike')
    },

    // Profile Analysis (50 GP)
    requestAnalysis: async (): Promise<{
      success: boolean
      reportId?: number
      analysis?: {
        overallScore: number
        strengths: string[]
        improvements: string[]
        tips: string[]
        photoTips: string[]
      }
      message: string
      pointsUsed?: number
      newBalance?: number
    }> => {
      return this.request('/premium/analysis', { method: 'POST' })
    },

    getAnalysisHistory: async (): Promise<{
      reports: Array<{
        id: number
        analysis: {
          overallScore: number
          strengths: string[]
          improvements: string[]
          tips: string[]
          photoTips: string[]
        }
        createdAt: string
      }>
      cost: number
    }> => {
      return this.request('/premium/analysis')
    },

    // Extra Match Slot (50 GP)
    getMatchSlotsInfo: async (): Promise<{
      currentMaxMatches: number
      activeMatches: number
      availableSlots: number
      canPurchaseMore: boolean
      cost: number
      maxPossible: number
    }> => {
      return this.request('/premium/extraslot')
    },

    purchaseExtraSlot: async (): Promise<{
      success: boolean
      message: string
      newMaxMatches?: number
      pointsUsed?: number
      newBalance?: number
    }> => {
      return this.request('/premium/extraslot', { method: 'POST' })
    },
  }
}


export const api = new ApiClient()

// Auto-set token from store
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('glovely-storage')
  if (stored) {
    try {
      const data = JSON.parse(stored)
      if (data.state?.token) {
        api.setToken(data.state.token)
      }
    } catch {
      // Ignore parse errors
    }
  }
}
